import { Add, ChevronLeft, Search } from '@mui/icons-material'
import {
  Box,
  Button,
  Divider,
  IconButton,
  Modal,
  Paper,
  TextField,
  Typography
} from '@mui/material'
import React from 'react'
import { connect } from 'react-redux'
import NewsAdminModal from 'components/admin/news-admin-modal'
import { NewsCard } from 'components/news'
import NivosPaginator from 'components/nivos-paginator'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as NewsOperations,
  operations as newsOperations
} from 'states/dux/nivos-news/operations'
import { Payload as NewsPayload } from 'states/dux/nivos-news/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { NivosNews } from 'types/nivos-news'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  user: UserPayload
  news: NewsPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    user: UserOperations
    news: NewsOperations
  }
}

interface OwnProps {
  onCloseContent: () => void
}

type AppProps = StateProps & DispatchProps & OwnProps

const NewsAdminFC: React.FC<any> = (props: AppProps) => {
  const { onCloseContent, news, user, operations, theme } = props
  const [activeNews, setActiveNews] = React.useState<NivosNews[]>([])
  const [scheduledNews, setScheduledNews] = React.useState<NivosNews[]>([])
  const [archivedNews, setArchivedNews] = React.useState<NivosNews[]>([])
  const [selectedNews, setSelectedNews] = React.useState<NivosNews>()
  const [searchKey, setSearchKey] = React.useState('')
  const [filtered, setFiltered] = React.useState(false)

  const emptyNews: NivosNews = {
    id: 0,
    title: '',
    message: '',
    rteMessage: '',
    date: new Date(),
    important: false,
    scheduled: false,
    active: false,
    archived: false,
    images: [],
    notifyGroup: [],
    notifyUsers: [],
    createdBy: user,
    scheduledAt: null,
    createdAt: new Date(),
    nudgedAt: new Date()
  }

  const _filterData = (): void => {
    void get(`/notification/?search=${searchKey}`).then(data => {
      const _ = data.map((_news: NivosNews) => objectifyDateStringFields(_news))
      operations.news.set(_)
      setFiltered(searchKey.trim() !== '')
    })
  }

  React.useEffect(() => {
    if (news.data.length === 0) {
      void get('/notification/').then(data => {
        const _ = data.map((_news: NivosNews) =>
          objectifyDateStringFields(_news)
        )
        operations.news.set(_)
      })
    }
  }, [])

  React.useEffect(() => {
    const _active = news.data.filter(i => i.active)
    const _scheduled = news.data.filter(i => i.scheduled && !i.active)
    const _archived = news.data.filter(i => i.archived)
    setActiveNews(_active)
    setScheduledNews(_scheduled)
    setArchivedNews(_archived)
  }, [news])

  const NewsDialogMenu: React.FC = () => {
    const [dialogLoading, setDialogLoading] = React.useState(false)
    return (
      <Modal open={false}>
        <Box
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'center'}
          alignItems={'center'}
          height={1}
        >
          <Paper
            variant={'elevation'}
            sx={{
              width: 'min-content',
              maxHeight: '95vh',
              overflow: 'auto',
              padding: 2
            }}
            elevation={8}
          >
            <Typography gutterBottom>Re-Activate News?</Typography>
            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'space-evenly'}
              width={300}
            >
              <Button
                variant={'contained'}
                disabled={dialogLoading}
                onClick={() => {
                  if (selectedNews !== undefined) {
                    setDialogLoading(true)
                    void get(`/notification/${selectedNews.id}/activate/`).then(
                      data => {
                        try {
                          operations.news.update(
                            objectifyDateStringFields(data)
                          )
                        } catch (e) {
                          console.log(e)
                        }
                        setDialogLoading(false)
                        setSelectedNews(undefined)
                      }
                    )
                  }
                }}
              >
                {dialogLoading ? 'Loading...' : 'Activate'}
              </Button>
              <Button
                disabled={dialogLoading}
                variant={'outlined'}
                onClick={() => setSelectedNews(undefined)}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        </Box>
      </Modal>
    )
  }

  return (
    <Box width={0.95} minHeight={'85vh'} marginX={'auto'}>
      <NewsAdminModal
        selectedNews={selectedNews}
        setSelectedNews={setSelectedNews}
      />
      <NewsDialogMenu />
      <Box display={'flex'} alignItems={'center'}>
        <IconButton
          size={'medium'}
          sx={{
            marginLeft: '-35px'
          }}
          onClick={() => {
            // Fetch new if current results are filtered
            if (filtered) {
              void get('/notification/').then(data => {
                const _ = data.map((news: NivosNews) =>
                  objectifyDateStringFields(news)
                )
                operations.news.set(_)
              })
            }
            onCloseContent()
          }}
        >
          <ChevronLeft fontSize={'large'} sx={{ fontSize: 45 }} />
        </IconButton>
        <Typography variant={'h4'}>
          <strong>NEWS</strong>
        </Typography>
        <Button
          variant={'outlined'}
          size={'large'}
          startIcon={<Add />}
          sx={{ borderRadius: 4, borderWidth: 2, margin: 2, marginLeft: 10 }}
          onClick={() => setSelectedNews(emptyNews)}
        >
          PUBLISH NEWS
        </Button>
        <Box flexGrow={1} />
        <Box>
          <TextField
            id={'search-news-title'}
            InputProps={{
              startAdornment: <Search />
            }}
            type={'text'}
            placeholder={'News title...'}
            value={searchKey}
            onChange={event => setSearchKey(event.currentTarget.value)}
            onKeyDown={event => {
              if (event.code === 'Enter') {
                _filterData()
              }
            }}
          />
        </Box>
      </Box>
      <Box padding={4}>
        <Box display={'flex'} width={1} minHeight={400}>
          <Box display={'flex'} flexDirection={'column'} width={0.5}>
            <Typography variant={'h4'} gutterBottom>
              ACTIVE NEWS
            </Typography>
            <Box width={'fit-content'}>
              {activeNews.length > 0 && (
                <NivosPaginator
                  hidePages
                  dataList={activeNews}
                  childElement={NewsCard}
                  itemsPerPage={3}
                  childPropSetter={(data, index) => {
                    return {
                      key: index,
                      news: data,
                      containerProps: {
                        onClick: () => setSelectedNews(data),
                        style: { cursor: 'pointer' }
                      }
                    }
                  }}
                />
              )}
              {activeNews.length === 0 && (
                <Typography
                  margin={3}
                  variant={'h4'}
                  align={'center'}
                  padding={4}
                >
                  No Data
                </Typography>
              )}
            </Box>
          </Box>
          <Box display={'flex'} flexDirection={'column'} width={0.5}>
            <Typography variant={'h4'} gutterBottom>
              UPCOMING NEWS
            </Typography>
            <Box width={'fit-content'}>
              {scheduledNews.length > 0 && (
                <NivosPaginator
                  hidePages
                  dataList={scheduledNews}
                  childElement={NewsCard}
                  itemsPerPage={3}
                  childPropSetter={(data, index) => {
                    return {
                      key: index,
                      news: data,
                      containerProps: {
                        onClick: () => setSelectedNews(data),
                        style: { cursor: 'pointer' }
                      }
                    }
                  }}
                />
              )}
              {scheduledNews.length === 0 && (
                <Typography
                  margin={9}
                  variant={'h4'}
                  align={'center'}
                  padding={3}
                >
                  No Data
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Box paddingY={3} />
        <Divider />
        <Box paddingY={3} />
        <Box>
          <Box display={'flex'} flexDirection={'column'} width={0.5}>
            <Typography variant={'h4'} gutterBottom>
              ARCHIVED NEWS
            </Typography>
            <Box width={'fit-content'}>
              {archivedNews.length > 0 && (
                <NivosPaginator
                  hidePages
                  dataList={archivedNews}
                  childElement={NewsCard}
                  itemsPerPage={3}
                  childPropSetter={(data, index) => {
                    return {
                      key: index,
                      news: data,
                      containerProps: {
                        onClick: () => setSelectedNews(data),
                        style: {
                          cursor: 'pointer',
                          backgroundColor: theme.quaternaryColor
                        }
                      }
                    }
                  }}
                />
              )}
              {archivedNews.length === 0 && (
                <Typography
                  margin={6}
                  variant={'h4'}
                  align={'center'}
                  padding={4}
                >
                  No Data
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    user: state.user,
    news: state.news
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  user: userOperations,
  news: newsOperations
})

const NewsAdmin = connect(
  mapStateToProps,
  mapDispatchToProps
)(NewsAdminFC) as React.FC<OwnProps>

export default NewsAdmin
